/* eslint @next/next/no-img-element: 0 */  // --> OFF

import classNames from "classnames";
import LoadingSpinner from "./LoadingSpinner";
import ImageEditor from "./ImageEditor";
import { useRef, useState } from "react";
import useRpc from "@/src/useRpc";
import Modal from "./Modal";
import rpc from "@/src/rpc";
import Tooltip from "./Tooltip";
import EditIcon from "./icons/EditIcon";
import TrashIcon from "./icons/TrashIcon";
import withLoading from "@/src/withLoading";
import PopupMenu from "./PopupMenu";
import Toggle from "./Toggle";

export default function ImageTemplateSettings ({org}) {
  const [imageTemplates, _err, reload] = useRpc('imageTemplates.all', {orgId: org.id})

  const [activeImageTemplate, setActiveImageTemplate] = useState(null)
  const isNewTemplate = useRef(false)

  async function handleCreate () {
    withLoading(async () => {
      const newTemplate = await rpc('imageTemplates.create', {orgId: org.id})
      isNewTemplate.current = true
      setActiveImageTemplate(newTemplate)
    })
  }

  async function deleteTemplate (template) {
    if (!confirm('Are you sure you want to delete this image template? This cannot be undone.')) return
    withLoading(async () => {
      await rpc('imageTemplates.remove', {id: template.id})
      await reload()
    })
  }

  async function editTemplate (template) {
    isNewTemplate.current = false
    setActiveImageTemplate(template)
  }

  async function onClose ({saved}) {
    const currentTemplate = activeImageTemplate
    setActiveImageTemplate(null)

    withLoading(async () => {
      // Don't keep a new template if they didn't save it
      if (isNewTemplate.current && !saved) {
        await rpc('imageTemplates.remove', {id: currentTemplate.id})
        await reload()
      } else if (saved) {
        await reload()
      }
    })
  }

  if (!imageTemplates) return <LoadingSpinner />

  // Note: Using min-height-110 to prevent weird layout flicker when hovering
  // over templates, as scrollbar turns on and off.

  return <>
    <section className="settings p-4 pb-6 flex-col gap-4 bg-f3f3f3 min-height-110">
      <button className="button is-primary" onClick={handleCreate}>
        Create Image Template
      </button>

      { imageTemplates.length === 0 &&
        <div className="flex-col gap-4 p-2">
          <p className="gray">Image templates can be used to create consistent backgrounds and layouts on your posts. You can create templates with different styles, colors, fonts, logos, and text placement.</p>
        </div>
      }
      <div className="flex-col gap-4">
        { imageTemplates.map(template => (
          <div key={template.id} className={classNames('image-template shadow-lg flex-col relative rounded overflow-hidden', template.status)}>

            <button className="image-edit-button bg-none no-border pointer absolute-full p-3 flex-row flex-justify-center flex-align-start transition opacity-40 hover_opacity-100" onClick={() => editTemplate(template)}>
              <div className={classNames("flex-row flex-center bold button px-2 rounded-lg shadow-xl")} style={{zIndex: 22}}>
                <EditIcon/> <span className="edit-button-label">Edit</span>
                <Tooltip>Edit this template</Tooltip>
              </div>
            </button>

            <div className={classNames("image-buttons flex-row spread z-1 absolute top left right p-3")}>
              <button className={classNames("flex-row flex-center bold button px-2 rounded-lg transition opacity-50 hover_danger hover_opacity-100 shadow-xl")} style={{zIndex: 22}} onClick={() => deleteTemplate(template)}>
                <TrashIcon/> <span className="hidden">Remove</span>
                <Tooltip>Delete</Tooltip>
              </button>
            </div>

            <div className={classNames("image-buttons flex-row spread z-1 absolute top right p-3")}>
              <div className="options">
                <PopupMenu tooltip="Options" className="px-2 transition opacity-50 hover_opacity-100 shadow-xl">
                  <div className='flex-col gap-4 spread px-1 py-2' style={{right: 406, top: 10}}>
                    <div className='flex-row gap-2 pr-3'>
                      <Toggle
                        label="Use for AI-generated posts"
                        checked={template.status == 'active'}
                        onChange={async (val) => {
                          const newStatus = template.status == 'active' ? 'draft' : 'active'
                          await rpc('imageTemplates.updateStatus', {id: template.id, status: newStatus})
                          await reload()
                        }}
                      />
                    </div>
                    <div className="border rounded text-sm p-3 dark-gray max-width-250">
                      { template.status == 'draft' ?
                        <p className="">
                          This template will only be available when manually editing posts.
                        </p>
                      :
                        <p className="">
                          AI will use this template when creating images for your posts.
                        </p>
                      }
                    </div>
                  </div>
                </PopupMenu>
              </div>
            </div>

            <a className='flex' onClick={() => editTemplate(template)}>
              <img src={template.image.thumbnailUrl} alt={template.description}/>
            </a>
          </div>
        ))}
      </div>

    </section>

    { activeImageTemplate &&
      <Modal visible>
        <div className="padded flex-col gap-4" style={{maxHeight: '95vh', display: 'flex'}}>

          <ImageEditor
            mode="template"
            image={activeImageTemplate.image}
            onSave={() => {
              reload()
              onClose({saved: true})
            }}
            onCancel={() => onClose({saved: false})}
          />

        </div>
      </Modal>
    }


    <style jsx>{`
      .image-buttons {
        pointer-events: none;
      }
      .image-buttons > .options,
      .image-buttons > button {
        pointer-events: all;
      }
      .image-edit-button .edit-button-label {
        max-width: 0;
        overflow: hidden;
        opacity: 0;
        padding: 0;
        transition: all 200ms ease-in-out;
      }
      .image-edit-button:hover .edit-button-label {
        max-width: 50px;
        padding: 0 0.25rem;
        opacity: 1;
        transition: all 400ms ease-in-out;
      }

      @media (hover: hover) and (pointer: fine) {
        .image-buttons {
          opacity: 0.8;
          transition: opacity 200ms ease-in-out;
        }
        .image-template:hover .image-buttons {
          opacity: 1;
        }
      }

    `}</style>

</>
}
